/**
 * AudioRecordingToggle (#597) — admin-only on/off control for per-user
 * mic-input audio capture. Shipped on the AdminUserDetail page.
 *
 * Privacy guard rail: the toggle UI surfaces the consent text inline so
 * the admin sees what they're enabling on every flip. The underlying
 * `users.audio_recording_enabled` flag defaults to FALSE (migration
 * 0064) — every user must be individually opted in by Bas or a
 * delegated admin.
 *
 * The toggle does NOT delete pre-existing clips when flipped OFF;
 * retention is a separate decision (manual purge for Alpha — see
 * #597 "Out of scope (v1)").
 */
import React, { useEffect, useState } from 'react';
import { adminApi } from './adminApi';

const CONSENT_TEXT = (
  'Enabling this records the user\'s voice on every call (every utterance,'
  + ' including health/financial/personal disclosures) for diagnostic'
  + ' playback by Amigo Mio admins. Voice is biometric data under GDPR;'
  + ' obtain explicit consent from the user (or their consenting family'
  + ' member) before turning this ON. Recordings can be deleted on'
  + ' request — auto-purge as code is a follow-up.'
);

export default function AudioRecordingToggle({ userId }) {
  const [enabled, setEnabled] = useState(null); // null = loading
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!userId) return;
    adminApi
      .getAudioRecordingFlag(userId)
      .then((d) => setEnabled(Boolean(d.enabled)))
      .catch((e) => setError(e.message));
  }, [userId]);

  async function flip(nextValue) {
    setPending(true);
    try {
      const res = await adminApi.setAudioRecordingFlag(userId, nextValue);
      setEnabled(Boolean(res.enabled));
    } catch (e) {
      setError(e.message);
    } finally {
      setPending(false);
    }
  }

  return (
    <div style={styles.container}>
      <div style={styles.title}>Mic-audio recording (Internal — #597)</div>
      <div style={styles.consent}>{CONSENT_TEXT}</div>
      {error && <div style={styles.error}>Error: {error}</div>}
      {enabled === null && !error && (
        <div style={styles.muted}>Loading…</div>
      )}
      {enabled !== null && (
        <div style={styles.row}>
          <span style={styles.state}>
            Current: <strong style={{ color: enabled ? '#27AE60' : '#aaa' }}>
              {enabled ? 'ON' : 'OFF'}
            </strong>
          </span>
          <button
            style={enabled ? styles.btnOff : styles.btnOn}
            disabled={pending}
            onClick={() => flip(!enabled)}
          >
            {pending ? '…' : enabled ? 'Turn OFF' : 'Turn ON (I have consent)'}
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    padding: '20px 24px',
    marginTop: 20,
  },
  title: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#2C2C2C',
    marginBottom: 10,
    letterSpacing: '-0.01em',
  },
  consent: {
    fontSize: '0.78rem',
    color: '#555',
    background: '#FFF8E1',
    border: '1px solid #FFE082',
    borderRadius: 8,
    padding: '10px 12px',
    marginBottom: 12,
    lineHeight: 1.45,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  state: { fontSize: '0.9rem', color: '#444' },
  btnOn: {
    background: '#27AE60',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnOff: {
    background: '#C0392B',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  error: { color: '#C0392B', fontSize: '0.85rem', marginBottom: 8 },
  muted: { color: '#aaa', fontSize: '0.85rem', fontStyle: 'italic' },
};
